import { ConnectProto } from "@racoon/api";
import { normalizeForSearch } from "@racoon/lib";

/** A truck's jobs in the period, grouped the way the rest of govdash counts them. */
export type JobSummary = {
  /** COMPLETED and FORCE_COMPLETED: a dispatcher's close counts as done, as on the monitor. */
  done: number;
  notDone: number;
  inProgress: number;
  /** The three groups together: a cancelled job was withdrawn, not failed. */
  total: number;
};

/** A truck that ran routes in the period, as its list card shows it. */
export type FleetTruck = {
  /** Truck id as a string — int64 ids can exceed Number.MAX_SAFE_INTEGER. */
  id: string;
  truck: ConnectProto.Truck;
  /** The MVO owning the truck: an auditor holds the MVO to account, not the driver. */
  organizationName: string;
  /** Cancelled routes are left out, as cancelled jobs are left out of the summary. */
  routeCount: number;
  summary: JobSummary;
};

const DONE: ReadonlySet<ConnectProto.JobStatus> = new Set([
  ConnectProto.JobStatus.COMPLETED,
  ConnectProto.JobStatus.FORCE_COMPLETED,
]);
const NOT_DONE: ReadonlySet<ConnectProto.JobStatus> = new Set([
  ConnectProto.JobStatus.NOT_COMPLETED,
]);
const IN_PROGRESS: ReadonlySet<ConnectProto.JobStatus> = new Set([
  ConnectProto.JobStatus.CREATED,
  ConnectProto.JobStatus.ASSIGNED,
  ConnectProto.JobStatus.ENROUTE,
  ConnectProto.JobStatus.ON_SERVICE,
]);

/** CANCELLED and a status the backend does not know (UNDEFINED) fall in no group. */
export function summarizeJobs(jobs: Pick<ConnectProto.Job, "status">[]): JobSummary {
  let done = 0;
  let notDone = 0;
  let inProgress = 0;
  for (const { status } of jobs) {
    if (DONE.has(status)) done++;
    else if (NOT_DONE.has(status)) notDone++;
    else if (IN_PROGRESS.has(status)) inProgress++;
  }
  return { done, notDone, inProgress, total: done + notDone + inProgress };
}

/** The truck a route ran on; routes still without a shift or truck have none. */
export function routeTruckId(route: ConnectProto.Route): string | undefined {
  const shift = route.driverOnShift;
  const id = shift?.truck?.id ?? shift?.truckId;
  return id ? String(id) : undefined;
}

/** Problem trucks first: by not-done jobs, most first, then by plate. */
function compareFleetTrucks(a: FleetTruck, b: FleetTruck): number {
  return (
    b.summary.notDone - a.summary.notDone ||
    a.truck.plateNumber.localeCompare(b.truck.plateNumber, "ru")
  );
}

/**
 * The trucks of the period's routes, one entry per truck, sorted problem trucks first.
 * The backend already narrowed each route's jobs to the caller's pickup locations, so the
 * summary only counts jobs in the auditor's area.
 */
export function buildFleet(routes: ConnectProto.Route[]): FleetTruck[] {
  const byTruck = new Map<string, { truck: ConnectProto.Truck; routes: ConnectProto.Route[] }>();
  for (const route of routes) {
    const id = routeTruckId(route);
    const truck = route.driverOnShift?.truck;
    if (!id || !truck) continue;
    const entry = byTruck.get(id);
    if (entry) entry.routes.push(route);
    else byTruck.set(id, { truck, routes: [route] });
  }

  return Array.from(byTruck, ([id, { truck, routes }]) => ({
    id,
    truck,
    organizationName: truck.ownerOrganization?.name ?? "",
    routeCount: routes.filter((route) => route.status !== ConnectProto.RouteStatus.CANCELLED)
      .length,
    summary: summarizeJobs(routes.flatMap((route) => route.jobs ?? [])),
  })).sort(compareFleetTrucks);
}

/** By plate, model or organization; an empty query keeps every truck. */
export function searchFleet(fleet: FleetTruck[], query: string): FleetTruck[] {
  const q = normalizeForSearch(query);
  if (!q) return fleet;
  return fleet.filter(
    ({ truck, organizationName }) =>
      normalizeForSearch(truck.plateNumber).includes(q) ||
      normalizeForSearch(truck.model).includes(q) ||
      normalizeForSearch(organizationName).includes(q),
  );
}
